/**
 * @file index.tsx
 *
 * @fileoverview Shows a video gallery. Takes a list of images (src) and renders them inside a gallery.
 */
import { useRef, useEffect } from "react";
import ReactImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import CloseIcon from "@jq-icons/Close";
import styles from "./videoGallery.module.scss";

interface VideoSrc {
  cover: string;
  src: string;
  type: "youtube" | "vimeo";
}

export interface VideoGalleryProps {
  videos: VideoSrc[];
  isGalleryOpen: boolean;
  /**
   * Index of the selected image when the gallery opens.
   */
  startIndex?: number;
  onClose: () => void;
  /**
   * The close icon title.
   *
   * @default 'X'
   */
  closeTitle?: string;
}

export const VideoGallery = ({
  videos,
  isGalleryOpen,
  startIndex = 0,
  onClose,
  closeTitle = "X",
}: VideoGalleryProps) => {
  const ref = useRef(null);

  useEffect(() => {
    if (isGalleryOpen) {
      (ref as any).current.toggleFullScreen();
      (ref as any)?.current?.slideToIndex(startIndex);
    }
  }, [isGalleryOpen, startIndex]);

  return (
    <div className={styles.videoGallery}>
      {isGalleryOpen ? (
        <ReactImageGallery
          ref={ref}
          showPlayButton={false}
          items={videos.map((video) => {
            return {
              original: video.cover,
              thumbnail: video.cover,
              type: video.type,
              src: video.src,
            };
          })}
          renderItem={(item: VideoSrc) => {
            switch (item.type) {
              case "youtube":
                return (
                  <div className="video-wrapper">
                    <iframe
                      title="youtube video"
                      width="560"
                      height="315"
                      src={item.src}
                    ></iframe>
                  </div>
                );
              case "vimeo":
                return (
                  <div className="video-wrapper">
                    <iframe
                      title="vimeo video"
                      width="560"
                      height="315"
                      src={item.src}
                    ></iframe>
                  </div>
                );

              default:
                return null;
            }
          }}
          onScreenChange={(fullscreen) => {
            if (!fullscreen) onClose();
          }}
          renderFullscreenButton={(onClick) => {
            return (
              <button className={styles.closeButton} onClick={onClick}>
                <CloseIcon
                  height="22"
                  width="22"
                  color="black"
                  title={closeTitle}
                />
              </button>
            );
          }}
        />
      ) : null}
    </div>
  );
};
